import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { ChevronLeftIcon, Squares2X2Icon, ClockIcon } from '@heroicons/react/24/outline';
import OldTable from './OldTable';
import Table from './Table';
import SummaryTable from './SummaryTable';
import SummaryChart from './SummaryChart';
import axiosInstance from '../../../axiosConfig';
import { useGlobalStore } from '../../../store/globalStore';

const DtausDirectDebitDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { search } = useLocation();
  const qs = new URLSearchParams(search);
  const title = qs.get('title') || '';
  const [showOldTable, setShowOldTable] = useState<boolean>(false);
  const [apiData, setApiData] = useState<any>(null);
  const setLoading = useGlobalStore((state) => state.setLoading);
  const isLoading = useGlobalStore((state) => state.isLoading);
  const [downloadingLastschrift, setDownloadingLastschrift] = useState(false);
  const [downloadingUeberweisung, setDownloadingUeberweisung] = useState(false);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        setLoading(true);
        const res = await axiosInstance.get(`/sepa-dateien/${id}`);
        // eslint-disable-next-line no-console
        console.log('SEPA Dateien Detail:', res.data);
        setApiData(res.data);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Fehler beim Abruf von SEPA-Dateien:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const extractFilename = (contentDisposition?: string, fallback: string = 'download.xml') => {
    if (!contentDisposition) return fallback;
    const match = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i.exec(contentDisposition);
    if (!match) return fallback;
    const filename = match[1]?.replace(/['"]/g, '');
    return filename || fallback;
  };

  const triggerDownload = (data: BlobPart, contentType: string | undefined, filename: string) => {
    const blob = new Blob([data], { type: contentType || 'application/xml' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadLastschrift = useCallback(async () => {
    if (!id) return;
    try {
      setDownloadingLastschrift(true);
      const res = await axiosInstance.get(`/sepa-dateien/${id}/lastschrift`, {
        responseType: 'blob',
      });
      const filename = extractFilename(
        (res.headers as any)?.['content-disposition'],
        `sepa_lastschrift_${id}.xml`,
      );
      triggerDownload(res.data, (res.headers as any)?.['content-type'], filename);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Download Lastschrift error:', error);
    } finally {
      setDownloadingLastschrift(false);
    }
  }, [id]);

  const handleDownloadUeberweisung = useCallback(async () => {
    if (!id) return;
    try {
      setDownloadingUeberweisung(true);
      const res = await axiosInstance.get(`/sepa-dateien/${id}/ueberweisung`, {
        responseType: 'blob',
      });
      const filename = extractFilename(
        (res.headers as any)?.['content-disposition'],
        `sepa_ueberweisung_${id}.xml`,
      );
      triggerDownload(res.data, (res.headers as any)?.['content-type'], filename);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Download Ueberweisung error:', error);
    } finally {
      setDownloadingUeberweisung(false);
    }
  }, [id]);

  return (
    <div className="px-6">
      <div className="bg-white border rounded-md p-4 relative">
        <div className="mb-2">
          <Link
            to="/dtaus-direct-debit"
            className="inline-flex cursor-pointer items-center gap-1 text-xs font-medium text-[#52A445] hover:underline"
          >
            <ChevronLeftIcon className="w-4 h-4" />
            Zurück zu SEPA Dateien
          </Link>
        </div>
        {title && <p className="text-xl font-semibold text-md">{title}</p>}
        <div className="mt-2 absolute right-4 top-4 flex items-center justify-end">
          <div className="inline-flex items-center rounded-md border border-gray-200 bg-white p-0.5 shadow-sm">
            <button
              type="button"
              onClick={() => setShowOldTable(false)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-sm border transition ${!showOldTable
                ? 'bg-[#52A445] text-white border-[#52A445] shadow-sm'
                : 'text-gray-700 hover:bg-gray-50 border-transparent'
                }`}
            >
              <Squares2X2Icon className="w-4 h-4" />
              Neues Design
            </button>
            <button
              type="button"
              onClick={() => setShowOldTable(true)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-sm border transition ${showOldTable
                ? 'bg-gray-800 text-white border-gray-800 shadow-sm'
                : 'text-gray-700 hover:bg-gray-50 border-transparent'
                }`}
            >
              <ClockIcon className="w-4 h-4" />
              Alte Ansicht
            </button>
          </div>
        </div>
        <style>
          {`
            /* Very light dotted background for table headers */
            .dtaus-head {
              background-color: #ffffff;
              background-image: radial-gradient(rgba(0,0,0,0.03) 0.6px, transparent 0.6px);
              background-size: 10px 10px;
              background-position: 0 0;
              box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.1);
            }
          `}
        </style>
        <div className="mt-2">
          {isLoading || !apiData ? (
            <div className="h-96 flex items-center justify-center">
              <div className="text-center">
                <div
                  className="mx-auto mb-3 h-6 w-6 rounded-full border-2 border-gray-300 border-t-[#52A445] animate-spin"
                  aria-label="Laden"
                  role="status"
                />
                <div className="text-sm text-gray-700">Bitte warten Sie.</div>
              </div>
            </div>
          ) : (
            <>
              {showOldTable ? <OldTable rows={apiData?.reports || []} /> : <Table items={apiData?.reports || []} />}
              <div className="flex my-10 items-center gap-10">
                <SummaryTable rows={apiData?.summary || []} />
                <SummaryChart summary={apiData?.summary || []} />
              </div>
              <button
                type="button"
                onClick={handleDownloadLastschrift}
                disabled={downloadingLastschrift}
                className="bg-[#52A445] text-white px-4 py-2 rounded-md disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {downloadingLastschrift ? 'Wird heruntergeladen…' : 'SEPA LASTSCHRIFT XML herunterladen'}
              </button>
              <button
                type="button"
                onClick={handleDownloadUeberweisung}
                disabled={downloadingUeberweisung}
                className="bg-[#52A445] text-white px-4 py-2 rounded-md ml-2 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {downloadingUeberweisung ? 'Wird heruntergeladen…' : 'SEPA ÜBERWEISUNG herunterladen'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DtausDirectDebitDetail;


